import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { animeList } from "@/mocks/anime";
import { newsList } from "@/mocks/news";

const socialLinks = [
  { label: "微博", handle: "@动漫天堂", icon: "ri-weibo-line", href: "https://51gglive.asia" },
  { label: "哔哩哔哩", handle: "动漫天堂官方", icon: "ri-bilibili-line", href: "https://www.bilibili.com" },
  { label: "小红书", handle: "动漫天堂", icon: "ri-book-2-line", href: "https://www.xiaohongshu.com" },
];

export default function AboutPage() {
  const location = useLocation();

  useDocumentMeta(
    "动漫天堂 - 关于我们",
    "了解动漫天堂的定位、内容范围与使用说明。我们是一个由动漫爱好者维护的内容站，专注动漫资料、剧情介绍与资讯整理。",
    "关于动漫天堂,动漫内容站,动漫资料,动漫剧情介绍,动漫天堂联系方式",
    "/about"
  );

  useEffect(() => {
    if (!location.hash) return;
    const target = document.getElementById(location.hash.replace("#", ""));
    if (target) {
      window.setTimeout(() => target.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    }
  }, [location.hash]);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteNavbar />

      <main className="flex-1 pt-16 md:pt-20">
        <section className="w-full border-b border-background-200/80 bg-background-100/60">
          <div className="mx-auto w-full max-w-page px-4 pb-10 pt-6 md:px-6 md:pb-14 md:pt-8">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <span className="text-foreground-800">关于动漫天堂</span>
            </nav>
            <h1 className="mt-4 max-w-3xl font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground-950 md:text-4xl">
              关于动漫天堂
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-8 text-foreground-600 md:text-base">
              动漫天堂是一个面向动漫爱好者的内容聚合站。我们把动漫作品的基础资料、剧情介绍和站内资讯整理在同一个结构里，让你在决定要不要看一部作品之前，先把它了解清楚。
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/category"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                开始浏览动漫
                <i className="ri-arrow-right-line" />
              </Link>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-mail-line" />
                联系我们
              </a>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-page px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.5fr_1fr] lg:gap-12">
            <div>
              <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
                我们做什么
              </h2>
              <div className="mt-4 space-y-4 text-sm leading-8 text-foreground-700 md:text-[15px]">
                <p>
                  动漫天堂不是播放站，也不是资源下载站。我们只做三件事：整理作品资料、写清楚每部作品的剧情梗概、把相关的资讯放在一起。这样当你看到一部感兴趣的作品时，不需要跳到很多个页面去拼凑信息。
                </p>
                <p>
                  站内的每一部作品都有独立的详情页，包含类型标签、地区、年份、更新状态、制作阵容与完整的剧情介绍。详情页下方会关联到与作品相关的资讯，以及同类型或同热度的推荐作品，方便你顺着一条线索继续看下去。
                </p>
                <p>
                  我们尽量让筛选更好用：类型、地区、年份都支持多选，排序可以按更新时间、热门度或评分切换。筛选结果会同步到页面链接里，复制地址就能分享给朋友。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 self-start">
              {[
                { value: String(animeList.length), label: "收录动漫", suffix: "部" },
                { value: String(newsList.length), label: "资讯文章", suffix: "篇" },
                { value: "3", label: "覆盖地区", suffix: "个" },
                { value: "8", label: "内容类型", suffix: "类" },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 px-4 py-5">
                  <p className="font-heading text-2xl font-extrabold text-primary-600">
                    {item.value}
                    <span className="ml-0.5 text-xs font-medium text-foreground-500">{item.suffix}</span>
                  </p>
                  <p className="mt-1.5 text-xs text-foreground-600">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100/70">
          <div className="mx-auto w-full max-w-page px-4 py-12 md:px-6 md:py-16">
            <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">内容范围</h2>
            <p className="mt-2 max-w-2xl text-sm leading-7 text-foreground-600">
              目前收录的内容覆盖日本、中国、美国三个地区的动漫作品，类型包括热血、恋爱、科幻、奇幻、治愈、悬疑、运动与日常等。
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
              {[
                {
                  icon: "ri-book-open-line",
                  title: "作品资料",
                  desc: "封面、类型、地区、年份、更新状态、制作公司与导演，一次看全。",
                },
                {
                  icon: "ri-file-text-line",
                  title: "剧情介绍",
                  desc: "每部作品都有完整的剧情梗概，重点写清楚故事从哪里开始、矛盾在哪里。",
                },
                {
                  icon: "ri-newspaper-line",
                  title: "资讯整理",
                  desc: "新番动态、制作访谈与剧情解析，并与相关作品互相打通。",
                },
              ].map((card) => (
                <div key={card.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-50 text-xl text-primary-600">
                    <i className={card.icon} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{card.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="mx-auto w-full max-w-page scroll-mt-24 px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
            <div>
              <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">联系方式</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                内容纠错、合作咨询或想推荐一部作品，都可以通过下面的方式找到我们。邮件通常会在两个工作日内回复。
              </p>
              <div className="mt-4 flex flex-col gap-3">
                <a
                  href="mailto:hello@anime-paradise.demo"
                  className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-4 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-lg text-primary-600">
                    <i className="ri-mail-line" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[11px] text-foreground-500">邮箱</span>
                    <span className="block truncate text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                      hello@anime-paradise.demo
                    </span>
                  </span>
                </a>
                <a
                  href="mailto:content@anime-paradise.demo"
                  className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-4 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-50 text-lg text-accent-600">
                    <i className="ri-file-edit-line" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[11px] text-foreground-500">内容纠错</span>
                    <span className="block truncate text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                      content@anime-paradise.demo
                    </span>
                  </span>
                </a>
              </div>
            </div>

            <div>
              <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">关注我们</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                日常更新会同步发布在社交平台，欢迎来聊番、投稿或指出资料里的错误。
              </p>
              <div className="mt-4 flex flex-col gap-3">
                {socialLinks.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-4 transition-colors hover:border-primary-300"
                  >
                    <span className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-700">
                        <i className={item.icon} />
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                          {item.label}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-foreground-500">{item.handle}</span>
                      </span>
                    </span>
                    <i className="ri-external-link-line text-base text-foreground-400 group-hover:text-primary-600" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="notice" className="w-full scroll-mt-24 bg-background-100/70">
          <div className="mx-auto w-full max-w-page px-4 py-12 md:px-6 md:py-16">
            <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">版权声明与使用说明</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-sm font-bold text-foreground-950">关于站内内容</h3>
                <ul className="mt-3 space-y-2.5 text-sm leading-7 text-foreground-600">
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-2.5 text-[6px] text-primary-500" />
                    本站所有动漫资料、剧情介绍与资讯文章均为演示内容，不代表真实作品或新闻事件。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-2.5 text-[6px] text-primary-500" />
                    站内使用的插图均为示意图片，仅用于展示页面结构，与真实作品无对应关系。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-2.5 text-[6px] text-primary-500" />
                    页面中出现的作品名称、评分与人气数据均为虚构示范，不构成任何推荐或评价。
                  </li>
                </ul>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-sm font-bold text-foreground-950">不提供的能力</h3>
                <ul className="mt-3 space-y-2.5 text-sm leading-7 text-foreground-600">
                  <li className="flex gap-2">
                    <i className="ri-close-line mt-0.5 text-base text-primary-500" />
                    本站不提供在线播放、视频嵌入或任何观看入口。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-close-line mt-0.5 text-base text-primary-500" />
                    本站不提供资源下载、网盘链接或会员付费服务。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-close-line mt-0.5 text-base text-primary-500" />
                    站内所有按钮与交互均为前端演示，不会产生真实订单或提交行为。
                  </li>
                </ul>
              </div>
            </div>
            <p className="mt-5 text-xs leading-6 text-foreground-500">
              © 2026 动漫天堂 · 本页面内容仅供学习与设计交流使用。如需引用站内排版或文案结构，请注明来源。
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}